import { take, select, type Effect, put, call } from 'redux-saga/effects';
import {
	type UIAnalyticsEvent,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import {
	ENTITY,
	ERROR_REPORTING_TEAM,
	PACKAGE_NAME,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant.tsx';
import { fireErrorAnalytics } from '@atlassian/jira-portfolio-3-portfolio/src/common/error/index.tsx';
import {
	CANCEL as CANCEL_REQUIRED_FIELDS,
	SUBMIT as SUBMIT_REQUIRED_FIELDS,
	FAIL as FAIL_REQUIRED_FIELDS,
	type LoadAction,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/update-jira/required-fields/actions.tsx';
import { getAnalyticsEvent } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/app/index.tsx';
import { updateIssueReferences } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/actions.tsx';
import isArjGicPrefilledDataHandlingEnabled from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/feature-flags/is-arj-gic-prefilled-data-handling-enabled.tsx';
import {
	add as addCommitWarning,
	remove as removeCommitWarning,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/update-jira/warnings/actions.tsx';
import { removeFromChanges } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/update-jira/changes/actions.tsx';
import { remove as removeOriginalIssue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/original-issues/actions.tsx';
import { getNotificationPreference } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/update-jira/index.tsx';
import { setRefetchBacklogOnClose } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/top/title-bar/update-jira/actions.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { getOnError, toErrorID } from '../../util.tsx';
import { inspectForCommitWarnings, defaultWarning } from '../../commit/warnings.tsx';
import { FAIL_COMMIT_REQUEST, HANDLE_COMMIT_RESPONSE } from '../../issue/types.tsx';
import { doCommitRequest } from './doCommitRequest.tsx';
import { doUpdateIssueKey } from './doUpdateIssueKey.tsx';
import { handleCommitResponse, isHandleCommitResponseMethod } from './handleCommitResponse.tsx';

export function* doHandleManualCreate({
	payload: { issueId, error: loadError },
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
}: LoadAction): Generator<Effect, void, any> {
	const manualCreateResult = yield take([
		SUBMIT_REQUIRED_FIELDS,
		CANCEL_REQUIRED_FIELDS,
		FAIL_REQUIRED_FIELDS,
	]);

	if (!expVal('unblock_plans_save_changes', 'isEnabled', false)) return;

	const notifyWatchers = yield select(getNotificationPreference);
	const analyticsEvent: UIAnalyticsEvent = yield select(getAnalyticsEvent);

	if (manualCreateResult.type === SUBMIT_REQUIRED_FIELDS) {
		const { generatedItemId } = manualCreateResult.payload;
		yield put(removeCommitWarning({ category: ENTITY.ISSUE, itemId: issueId }));

		// isArjGicPrefilledDataHandlingEnabled clean up , this should become a constant
		let commitRetryResponse;

		if (!!generatedItemId && generatedItemId !== issueId) {
			yield put(updateIssueReferences({ oldId: issueId, newId: generatedItemId }));
		}

		if (isArjGicPrefilledDataHandlingEnabled()) {
			commitRetryResponse = yield call(doUpdateIssueKey, issueId, generatedItemId);
		} else {
			commitRetryResponse = yield call(doCommitRequest, issueId, notifyWatchers, generatedItemId);
		}

		if (commitRetryResponse.type === HANDLE_COMMIT_RESPONSE) {
			const { error, entity } = commitRetryResponse.payload;
			const internalEntity = entity && entity.entity;
			const warnings = yield call(inspectForCommitWarnings, internalEntity, error);

			if (warnings.length) {
				fireOperationalAnalytics(
					analyticsEvent,
					'advancedRoadmaps issueCommitManualCreateFailedWithWarnings',
					{ error: error || loadError?.error },
				);

				yield put(
					addCommitWarning({
						category: ENTITY.ISSUE,
						itemId: issueId,
						warnings,
					}),
				);
			} else {
				fireOperationalAnalytics(
					analyticsEvent,
					'advancedRoadmaps issueCommitManualCreateSuccess',
					{ error: loadError?.error },
				);

				yield put(setRefetchBacklogOnClose(true));
				yield put(removeFromChanges({ issueId }));
				yield put(removeOriginalIssue([issueId]));
			}
		} else if (commitRetryResponse.type === FAIL_COMMIT_REQUEST) {
			yield put(
				addCommitWarning({
					category: ENTITY.ISSUE,
					itemId: issueId,
					warnings: [defaultWarning],
				}),
			);
		}
	} else if (manualCreateResult.type === CANCEL_REQUIRED_FIELDS) {
		fireOperationalAnalytics(analyticsEvent, 'advancedRoadmaps issueCommitManualCreateCancelled', {
			error: loadError?.error,
		});
	} else if (manualCreateResult.type === FAIL_REQUIRED_FIELDS) {
		fireOperationalAnalytics(analyticsEvent, 'advancedRoadmaps issueCommitManualCreateFailed', {
			error: loadError?.error,
		});

		const message = yield call(
			!isHandleCommitResponseMethod(handleCommitResponse) &&
				handleCommitResponse.text.bind(handleCommitResponse),
		);

		const error = new Error(message);
		const errorPayload = {
			error,
			meta: {
				id: toErrorID(error, 'manual-create-issue-failed'),
				packageName: PACKAGE_NAME,
				teamName: ERROR_REPORTING_TEAM,
			},
			sendToPrivacyUnsafeSplunk: true,
		};
		if (fg('plans-failure-tracking-show-error-flag')) {
			const onError = yield call(getOnError);
			onError(errorPayload);
		} else {
			fireErrorAnalytics(errorPayload);
		}
	}
}
